import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import Select from 'react-select';

import api from '../../lib/api';
import {
  findOrgClientOption,
  mapOrgClientsToPickerOptions,
} from '../../lib/orgClientsPicker';

function optionLabel(opt, preferPaidHint) {
  if (!opt) return '';
  let extra = '';
  if (!opt.isLinked) extra += ' · нет аккаунта';
  if (preferPaidHint && opt.isLinked && !opt.hasPaidAbonementForSection) extra += ' · нет абонемента';
  return `${opt.label}${extra}`;
}

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

/**
 * Один combobox: поиск + выбор клиента организации.
 */
class ComplexClientPicker extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    options: PropTypes.array,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    helperText: PropTypes.string,
    emptyText: PropTypes.string,
    loading: PropTypes.bool,
    preferPaidHint: PropTypes.bool,
    requireLinked: PropTypes.bool,
    selectId: PropTypes.string,
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCreated: PropTypes.func,
  };

  static defaultProps = {
    options: [],
    value: '',
    label: 'Клиент',
    disabled: false,
    helperText: '',
    emptyText: 'Пока нет клиентов организации. Добавьте карточку ниже.',
    loading: false,
    preferPaidHint: false,
    requireLinked: false,
    selectId: 'complex-client',
    orgId: null,
    onCreated: null,
  };

  state = {
    showCreate: false,
    newName: '',
    newPhone: '',
    createError: '',
    createLoading: false,
  };

  handleSelect = (option) => {
    if (!option) {
      this.props.onChange('', null);
      return;
    }
    const opt = findOrgClientOption(this.props.options, option.value);
    this.props.onChange(option.value, opt);
  };

  filterOption = (option, filterString) => {
    const q = String(filterString || '').trim().toLowerCase();
    if (!q) return true;
    const hay = `${option.label || ''} ${option.name || ''} ${option.phone || ''}`.toLowerCase();
    if (hay.indexOf(q) >= 0) return true;
    const qDigits = digits(q);
    if (qDigits.length >= 3) {
      const phoneDigits = digits(`${option.phone || ''} ${option.label || ''}`);
      if (phoneDigits.indexOf(qDigits) >= 0) return true;
    }
    return false;
  };

  createClient = async () => {
    const orgId = Number(this.props.orgId);
    const name = (this.state.newName || '').trim();
    const phone = (this.state.newPhone || '').trim();
    if (!orgId) {
      this.setState({ createError: 'Нет организации у клуба' });
      return;
    }
    if (!name || !phone) {
      this.setState({ createError: 'Укажите имя и телефон' });
      return;
    }
    this.setState({ createLoading: true, createError: '' });
    try {
      const created = await api.createOrganizationClient(orgId, {
        displayName: name,
        phone,
      });
      const mapped = mapOrgClientsToPickerOptions([created])[0] || null;
      this.setState({
        createLoading: false,
        showCreate: false,
        newName: '',
        newPhone: '',
      });
      if (typeof this.props.onCreated === 'function') {
        await this.props.onCreated(created, mapped);
      }
      if (mapped) {
        this.props.onChange(mapped.key, mapped);
      }
    } catch (error) {
      this.setState({
        createLoading: false,
        createError: error.message || 'Не удалось создать карточку',
      });
    }
  };

  render() {
    const {
      classes,
      options,
      value,
      label,
      disabled,
      helperText,
      emptyText,
      loading,
      preferPaidHint,
      requireLinked,
      orgId,
    } = this.props;
    const { showCreate, newName, newPhone, createError, createLoading } = this.state;
    const currentOpt = findOrgClientOption(options, value);
    const selectOptions = (options || []).map((opt) => ({
      value: opt.key,
      label: optionLabel(opt, preferPaidHint),
      name: opt.name || '',
      phone: opt.phone || '',
    }));
    const selectedValue = currentOpt
      ? { value: currentOpt.key, label: optionLabel(currentOpt, preferPaidHint) }
      : (value ? { value: String(value), label: 'Текущий выбор' } : null);
    const selectedUnlinked = currentOpt && !currentOpt.isLinked;
    const noResults = loading
      ? 'Загрузка…'
      : ((options || []).length === 0 ? emptyText : 'Никого не найдено');

    return (
      <div className={classes.root}>
        <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>
          {label}
        </Typography>
        <Select
          className={classes.combobox}
          placeholder="Начните вводить имя или телефон"
          value={selectedValue}
          onChange={this.handleSelect}
          options={selectOptions}
          disabled={disabled || loading}
          clearable
          searchable
          openOnFocus
          ignoreAccents={false}
          noResultsText={noResults}
          filterOption={this.filterOption}
          menuContainerStyle={{ zIndex: 3000 }}
        />
        {preferPaidHint ? (
          <Typography variant="caption" color="textSecondary" className={classes.hint}>
            Сначала те, у кого есть оплаченный абонемент на секцию
          </Typography>
        ) : null}
        {requireLinked && selectedUnlinked && (
          <Typography variant="caption" color="error" className={classes.hint}>
            Нет аккаунта в приложении. Абонемент, группу и персональную можно оформить после визита или заявки клиента.
          </Typography>
        )}
        {(options || []).length === 0 && !loading && (
          <Typography variant="caption" color="textSecondary" className={classes.hint}>
            {emptyText}
          </Typography>
        )}
        {helperText ? (
          <Typography variant="caption" color="textSecondary" className={classes.hint}>
            {helperText}
          </Typography>
        ) : null}
        {orgId ? (
          <div className={classes.createBox}>
            {showCreate ? (
              <div>
                <TextField
                  margin="dense"
                  label="Имя"
                  value={newName}
                  onChange={(e) => this.setState({ newName: e.target.value, createError: '' })}
                  fullWidth
                  disabled={disabled || createLoading}
                />
                <TextField
                  margin="dense"
                  label="Телефон"
                  value={newPhone}
                  onChange={(e) => this.setState({ newPhone: e.target.value, createError: '' })}
                  fullWidth
                  disabled={disabled || createLoading}
                />
                {createError ? (
                  <Typography variant="caption" color="error" className={classes.hint}>
                    {createError}
                  </Typography>
                ) : null}
                <div className={classes.createActions}>
                  <Button size="small" onClick={() => this.setState({ showCreate: false, createError: '' })} disabled={createLoading}>
                    Отмена
                  </Button>
                  <Button size="small" color="primary" onClick={this.createClient} disabled={disabled || createLoading}>
                    {createLoading ? 'Сохранение…' : 'Сохранить карточку'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="small"
                color="primary"
                disabled={disabled}
                onClick={() => this.setState({ showCreate: true })}
              >
                Новый клиент
              </Button>
            )}
          </div>
        ) : null}
      </div>
    );
  }
}

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  fieldLabel: {
    display: 'block',
    marginBottom: 2,
  },
  combobox: {
    marginBottom: theme.spacing(0.5),
  },
  hint: {
    display: 'block',
    marginTop: theme.spacing(0.5),
  },
  createBox: {
    marginTop: theme.spacing(0.5),
  },
  createActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
  },
});

export default withStyles(styles)(ComplexClientPicker);
